import { Typography, Box } from '@mui/material';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import type { IAssistantScriptDialogProps } from '@shared/interfaces/assistantScripts';
import { PollyLanguage } from '@shared/interfaces/polly';
import {
  CloseButton,
  ModalTitle,
  PrimaryButton,
  CancelButton,
  ScriptsDialog,
  ScriptsModalContent,
  ScriptsModalHeader,
  HeaderTitleRow,
  HeaderActionsRow,
  UnsavedChangesRow,
  UnsavedChangesDot,
  UnsavedChangesText,
  ScriptsModalBody,
  StepsHeaderRow,
  StepsTitle,
  AddStepButton,
  FixedStepsBar,
  StepsContainer,
  ScriptsModalActions,
} from './AssistantScriptDialog.styled';
import { DEFAULT_SCRIPT_LANGUAGES } from './constants';
import LanguagePickerDialog from './LanguagePickerDialog';
import LanguageTabs from './LanguageTabs';
import TriggerSection from './TriggerSection';
import ScriptStepItem from './ScriptStepItem';
import useAudioPreview from './hooks/useAudioPreview';
import useScriptSteps from './hooks/useScriptSteps';

const AssistantScriptDialog = ({
  open,
  assistantScript,
  trigger,
  defaultTriggerType,
  title,
  engine,
  voiceId,
  baseLanguage,
  languages,
  voicesByLanguage,
  mapName,
  poiId,
  brandingId,
  paperRef,
  modalTheme = 'light',
  userFirstName,
  onGenerateAudio,
  onTranslate,
  onSave,
  onClose,
}: IAssistantScriptDialogProps) => {
  const { t } = useTranslation('common');
  const effectiveBaseLanguage = baseLanguage ?? PollyLanguage.ENGLISH;
  const scriptLanguages = useMemo(() => {
    const defaultAdditional = DEFAULT_SCRIPT_LANGUAGES.filter(
      (l) => l !== PollyLanguage.ENGLISH,
    );
    const additional = (languages ?? defaultAdditional).filter(
      (l) => l !== effectiveBaseLanguage,
    );
    return [effectiveBaseLanguage, ...additional];
  }, [effectiveBaseLanguage, languages]);

  const scriptStepsEndRef = useRef<null | HTMLDivElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const stepsHeaderRef = useRef<HTMLDivElement>(null);

  const [isAddStepFixed, setIsAddStepFixed] = useState(false);
  const [fixedAddStepPos, setFixedAddStepPos] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const [activeLanguage, setActiveLanguage] = useState<PollyLanguage>(
    effectiveBaseLanguage,
  );
  const [translateMenuAnchor, setTranslateMenuAnchor] =
    useState<HTMLElement | null>(null);
  // The button-label language tabs render a second, independent instance of
  // the dropdown menu, so it needs its own anchor to avoid both menus opening
  // from a single click.
  const [buttonTranslateMenuAnchor, setButtonTranslateMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const [langPickerScope, setLangPickerScope] = useState<'steps' | 'button'>(
    'steps',
  );
  const [pickedLangs, setPickedLangs] = useState<PollyLanguage[]>([]);

  const {
    scriptSteps,
    stepExpanded,
    stepUserInputModes,
    stepsModified,
    isSubmitting,
    isTranslating,
    triggerType,
    setTriggerType,
    uiClickButtonLabel,
    setUiClickButtonLabel,
    uiClickButtonLabelTranslations,
    updateUiClickButtonLabelTranslation,
    interruptMode,
    setInterruptMode,
    bridgeRegionGaps,
    setBridgeRegionGaps,
    allowSkipDialog,
    setAllowSkipDialog,
    playbackPolicy,
    setPlaybackPolicy,
    skipFirstSession,
    setSkipFirstSession,
    formik,
    addNewStep,
    removeStep,
    updateStep,
    updateStepTranslation,
    moveStepUp,
    moveStepDown,
    toggleStepExpanded,
    handleUserInputModeChange,
    handleTranslateTo,
    handleTranslateButtonLabelTo,
  } = useScriptSteps({
    assistantScript,
    trigger,
    defaultTriggerType,
    title,
    mapName,
    brandingId,
    poiId,
    scriptStepsEndRef,
    onSave,
    onTranslate,
  });

  const { audioPlaybackKey, isAudioPlaying, playAudio, stopAudio } =
    useAudioPreview({
      activeLanguage,
      effectiveBaseLanguage,
      engine,
      voiceId,
      voicesByLanguage,
      onGenerateAudio,
    });

  const nonBaseLangs = useMemo(
    () => scriptLanguages.slice(1),
    [scriptLanguages],
  );

  useEffect(() => {
    if (!open) {
      setIsAddStepFixed(false);
      return;
    }

    const handleScroll = () => {
      const sentinel = stepsHeaderRef.current;
      const container = modalBodyRef.current;

      if (!sentinel || !container) {
        return;
      }

      const sentinelRect = sentinel.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const shouldBeFixed = sentinelRect.bottom < containerRect.top;

      setIsAddStepFixed(shouldBeFixed);

      if (shouldBeFixed) {
        setFixedAddStepPos({
          top: containerRect.top,
          left: containerRect.left,
          width: containerRect.width,
        });
      }
    };

    let container: HTMLDivElement | null = null;

    const raf = requestAnimationFrame(() => {
      container = modalBodyRef.current;

      if (!container) {
        return;
      }

      container.addEventListener('scroll', handleScroll);
    });

    return () => {
      cancelAnimationFrame(raf);
      container?.removeEventListener('scroll', handleScroll);
    };
  }, [open]);

  const hasChanges = formik.dirty || stepsModified;

  const renderLanguageTabs = (
    scope: 'steps' | 'button',
    menuAnchor: HTMLElement | null,
    onMenuOpen: (anchor: HTMLElement) => void,
    onMenuClose: () => void,
  ) => (
    <LanguageTabs
      scriptLanguages={scriptLanguages}
      effectiveBaseLanguage={effectiveBaseLanguage}
      activeLanguage={activeLanguage}
      scriptSteps={scriptSteps}
      scope={scope}
      uiClickButtonLabel={uiClickButtonLabel}
      uiClickButtonLabelTranslations={uiClickButtonLabelTranslations}
      nonBaseLangs={nonBaseLangs}
      isTranslating={isTranslating}
      translateMenuAnchor={menuAnchor}
      onActiveLanguageChange={setActiveLanguage}
      onTranslateAll={() =>
        scope === 'button'
          ? handleTranslateButtonLabelTo(nonBaseLangs)
          : handleTranslateTo(nonBaseLangs)
      }
      onTranslateMenuOpen={onMenuOpen}
      onTranslateMenuClose={onMenuClose}
      onPickLanguages={() => {
        setPickedLangs([]);
        setLangPickerScope(scope);
        setLangPickerOpen(true);
      }}
    />
  );

  return (
    <>
      <ScriptsDialog
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') {
            return;
          }

          onClose();
        }}
        fullWidth
        $theme={modalTheme}
        PaperProps={{ ref: paperRef }}
      >
        <ScriptsModalContent>
          <ScriptsModalHeader>
            <HeaderTitleRow>
              <ArticleOutlinedIcon
                sx={{ color: 'primary.main', fontSize: 22 }}
              />
              <ModalTitle sx={{ fontWeight: 400 }}>
                {!assistantScript?.scriptId
                  ? t('assistant.scriptDialog.createScript')
                  : t('assistant.scriptDialog.updateScript')}
              </ModalTitle>
            </HeaderTitleRow>
            <HeaderActionsRow>
              {hasChanges && (
                <UnsavedChangesRow>
                  <UnsavedChangesDot />
                  <UnsavedChangesText>{t('assistant.scriptDialog.unsavedChanges')}</UnsavedChangesText>
                </UnsavedChangesRow>
              )}
              <CloseButton onClick={onClose} size="small">
                <CloseIcon />
              </CloseButton>
            </HeaderActionsRow>
          </ScriptsModalHeader>

          <ScriptsModalBody ref={modalBodyRef}>
            {/* Trigger type */}
            <TriggerSection
              triggerType={triggerType}
              title={title}
              assistantScript={assistantScript}
              mapName={mapName}
              activeLanguage={activeLanguage}
              effectiveBaseLanguage={effectiveBaseLanguage}
              uiClickButtonLabel={uiClickButtonLabel}
              uiClickButtonLabelTranslations={uiClickButtonLabelTranslations}
              interruptMode={interruptMode}
              bridgeRegionGaps={bridgeRegionGaps}
              allowSkipDialog={allowSkipDialog}
              playbackPolicy={playbackPolicy}
              skipFirstSession={skipFirstSession}
              onTriggerTypeChange={setTriggerType}
              onUiClickButtonLabelChange={setUiClickButtonLabel}
              onUiClickButtonLabelTranslationChange={
                updateUiClickButtonLabelTranslation
              }
              languageTabs={renderLanguageTabs(
                'button',
                buttonTranslateMenuAnchor,
                setButtonTranslateMenuAnchor,
                () => setButtonTranslateMenuAnchor(null),
              )}
              onInterruptModeChange={setInterruptMode}
              onBridgeRegionGapsChange={setBridgeRegionGaps}
              onAllowSkipDialogChange={setAllowSkipDialog}
              onPlaybackPolicyChange={setPlaybackPolicy}
              onSkipFirstSessionChange={setSkipFirstSession}
            />

            {/* Language tabs (script steps) */}
            {renderLanguageTabs(
              'steps',
              translateMenuAnchor,
              setTranslateMenuAnchor,
              () => setTranslateMenuAnchor(null),
            )}

            {/* Script steps */}
            <Box>
              <StepsHeaderRow
                ref={stepsHeaderRef}
                sx={{ visibility: isAddStepFixed ? 'hidden' : 'visible' }}
              >
                <Box>
                  <StepsTitle>{t('assistant.scriptDialog.scriptSteps')}</StepsTitle>
                  {scriptSteps.length > 1 && (
                    <Typography variant="caption" color="text.secondary">
                      {t('assistant.scriptDialog.stepsOrderHint')}
                    </Typography>
                  )}
                </Box>
                <AddStepButton
                  variant="outlined"
                  size="small"
                  onClick={addNewStep}
                >
                  <AddIcon sx={{ fontSize: 15, mr: 0.5 }} /> {t('assistant.scriptDialog.addStep')}
                </AddStepButton>
              </StepsHeaderRow>

              {isAddStepFixed && (
                <FixedStepsBar
                  sx={{
                    top: fixedAddStepPos.top,
                    left: fixedAddStepPos.left,
                    width: fixedAddStepPos.width,
                  }}
                >
                  <Box>
                    <StepsTitle>{t('assistant.scriptDialog.scriptSteps')}</StepsTitle>
                    {scriptSteps.length > 1 && (
                      <Typography variant="caption" color="text.secondary">
                        {t('assistant.scriptDialog.stepsOrderHintShort')}
                      </Typography>
                    )}
                  </Box>
                  <AddStepButton
                    variant="outlined"
                    size="small"
                    onClick={addNewStep}
                  >
                    <AddIcon sx={{ fontSize: 15, mr: 0.5 }} /> {t('assistant.scriptDialog.addStep')}
                  </AddStepButton>
                </FixedStepsBar>
              )}

              <StepsContainer>
                {scriptSteps.map((step, index) => (
                  <ScriptStepItem
                    key={index}
                    step={step}
                    index={index}
                    isFirst={index === 0}
                    isLast={index === scriptSteps.length - 1}
                    expanded={stepExpanded[index] ?? true}
                    activeLanguage={activeLanguage}
                    effectiveBaseLanguage={effectiveBaseLanguage}
                    stepUserInputMode={stepUserInputModes[index] ?? 'none'}
                    audioPlaybackKey={audioPlaybackKey}
                    isAudioPlaying={isAudioPlaying}
                    userFirstName={userFirstName}
                    mapName={mapName}
                    onToggleExpanded={() => toggleStepExpanded(index)}
                    onMoveUp={() => moveStepUp(index)}
                    onMoveDown={() => moveStepDown(index)}
                    onUpdateStep={(data) => updateStep(index, data)}
                    onUpdateStepTranslation={(lang, value) =>
                      updateStepTranslation(index, lang, value)
                    }
                    onUserInputModeChange={(mode) =>
                      handleUserInputModeChange(index, mode)
                    }
                    onRemove={() => removeStep(index)}
                    onPlay={playAudio}
                    onStop={stopAudio}
                  />
                ))}
              </StepsContainer>
              <div ref={scriptStepsEndRef} />
            </Box>
          </ScriptsModalBody>

          <ScriptsModalActions>
            <CancelButton onClick={onClose}>{t('assistant.scriptDialog.cancel')}</CancelButton>
            <PrimaryButton
              onClick={() => formik.handleSubmit()}
              disabled={
                !((formik.dirty && formik.isValid) || scriptSteps.length > 0) ||
                isSubmitting
              }
            >
              {!assistantScript?.scriptId
                ? t('assistant.scriptDialog.createScript')
                : t('assistant.scriptDialog.saveChanges')}
            </PrimaryButton>
          </ScriptsModalActions>
        </ScriptsModalContent>
      </ScriptsDialog>

      <LanguagePickerDialog
        open={langPickerOpen}
        nonBaseLangs={nonBaseLangs}
        pickedLangs={pickedLangs}
        isTranslating={isTranslating}
        onPickedLangsChange={setPickedLangs}
        onClose={() => setLangPickerOpen(false)}
        onTranslate={
          langPickerScope === 'button'
            ? handleTranslateButtonLabelTo
            : handleTranslateTo
        }
      />
    </>
  );
};

export default AssistantScriptDialog;
